import React from 'react';
import { useLabelStore } from '../store/useLabelStore';
import RegulatoryTokenSection from './RegulatoryTokenSection';
import AgentKeySection from './AgentKeySection';
import { sortirFondPerduDuFormat } from '../utils/formatFini';

const NumInput = ({ value, onChange, min = null, max = null, step = 1 }) => (
    <div className="flex bg-black/30 border border-white/5 rounded-lg overflow-hidden shadow-inner hover:border-forge-sky/30 focus-within:border-forge-sky/40 focus-within:shadow-[0_0_6px_rgba(var(--forge-sky),0.12)] transition-all duration-200">
        <button type="button" className="px-3 py-1.5 hover:bg-forge-sky/15 text-white/40 hover:text-forge-sky transition-all duration-200 font-bold text-sm"
            onClick={() => { const v = Number((value - step).toFixed(2)); if (min === null || v >= min) onChange(v); }}>-</button>
        <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))}
            className="w-16 bg-transparent text-white text-center outline-none text-xs font-mono font-bold tracking-widest [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
        <button type="button" className="px-3 py-1.5 hover:bg-forge-sky/15 text-white/40 hover:text-forge-sky transition-all duration-200 font-bold text-sm"
            onClick={() => { const v = Number((value + step).toFixed(2)); if (max === null || v <= max) onChange(v); }}>+</button>
    </div>
);

const mm = (n) => String(Math.round(n * 100) / 100).replace('.', ',');

/**
 * Réparer un document dont le format COMPREND déjà son fond perdu.
 *
 * Jusqu'au 2 octobre 2026, l'import d'un PDF à fond perdu prenait la page
 * entière pour format : tous les documents nés d'un tel import — dont les
 * gabarits de l'atelier, 1,5 mm par côté — portent leur fond perdu DANS le
 * format, avec `bleed_mm` à 0. Leur PDF sort sans fond perdu déclaré, et les
 * traits de coupe se posent autour de la page au lieu du format fini (retour de
 * Louise, 2026-10-01). Ce bouton resserre le format et déclare le fond perdu,
 * sans rien déplacer sur la page (cf. utils/formatFini::sortirFondPerduDuFormat).
 */
const SortirFondPerdu = () => {
    const [valeur, setValeur] = React.useState(1.5);
    const appliquer = () => {
        const st = useLabelStore.getState();
        const r = sortirFondPerduDuFormat(st.canvas, st.elements, valeur);
        if (!r) { window.alert(`${mm(valeur)} mm de chaque côté ne laisse pas de format.`); return; }
        const ok = window.confirm(
            `Le format passe de ${mm(st.canvas.width_mm)} × ${mm(st.canvas.height_mm)} mm `
            + `à ${mm(r.canvas.width_mm)} × ${mm(r.canvas.height_mm)} mm, avec ${mm(valeur)} mm de fond perdu autour.\n\n`
            + `Rien ne bouge sur la page : le PDF saura enfin où est le format fini. Annulable par Cmd+Z.`);
        if (!ok) return;
        const patches = {};
        for (const el of r.elements) {
            if (typeof el.x === 'number' || typeof el.y === 'number') patches[el.id] = { x: el.x, y: el.y };
        }
        st.patchElements(patches);
        st.setCanvas(r.canvas);
        setTimeout(() => useLabelStore.getState()._pushState?.('Fond perdu sorti du format'), 0);
    };
    return (
        <div className="bg-black/20 border border-white/5 rounded-lg p-3 shadow-inner"
            title="Pour un document dont le format comprend déjà le fond perdu : un PDF importé avant le 2 octobre 2026, ou un gabarit de l'atelier. Le format se resserre et le fond perdu est déclaré, sans que rien ne bouge sur la page.">
            <p className="text-forge-rose/70 font-bold text-[9px] uppercase tracking-widest mb-2">Le fond perdu est dans le format ?</p>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                    <NumInput value={valeur} onChange={setValeur} min={0.5} max={10} step={0.5} />
                    <span className="text-forge-rose/40 text-[9px]">mm</span>
                </div>
                <button type="button" onClick={appliquer}
                    className="px-3 py-1.5 rounded-lg border border-forge-rose/30 bg-forge-rose/10 hover:bg-forge-rose/20 text-forge-rose text-[9px] font-bold uppercase tracking-widest transition-colors">
                    Le sortir du format
                </button>
            </div>
        </div>
    );
};

const Toggle = ({ label, checked, onChange }) => (
    <label className="flex items-center justify-between cursor-pointer group py-1.5 px-1 rounded-lg hover:bg-forge-mint/5 transition-all duration-200">
        <span className="text-[10px] text-forge-muted tracking-widest uppercase font-bold group-hover:text-forge-mint transition-colors duration-200">{label}</span>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="accent-forge-accent cursor-pointer w-3.5 h-3.5" />
    </label>
);

export default function PreferencesModal({ isOpen, onClose }) {
    const { canvas, setCanvas, uiTheme, setUiTheme, autoFitOnImport, setAutoFitOnImport } = useLabelStore();
    if (!isOpen) return null;

    // 4 themes sombres et 4 themes clairs (memes palettes accent, mais
    // bg/text inversés). Le slider de luminosité a été retiré.
    const THEMES_DARK = [
        { id: 'forge-dark', name: 'Carbone', color: '#1e1e24', accent: '#eab308' },
        { id: 'forge-dark-deep', name: 'Carbone profond', color: '#0e0e11', accent: '#eab308' },
        { id: 'forge-slate', name: 'Ardoise', color: '#202a3e', accent: '#38bdf8' },
        { id: 'forge-monochrome', name: 'Neutre', color: '#303030', accent: '#73737d' },
        { id: 'forge-creative', name: 'Créatif', color: '#2a203a', accent: '#c084fc' },
    ];
    const THEMES_LIGHT = [
        { id: 'forge-dark-light', name: 'Carbone clair', color: '#f4f2ec', accent: '#d97706' },
        { id: 'forge-slate-light', name: 'Ardoise clair', color: '#eef4fc', accent: '#0ea5e9' },
        { id: 'forge-monochrome-light', name: 'Neutre clair', color: '#f5f5f5', accent: '#82828c' },
        { id: 'forge-creative-light', name: 'Créatif clair', color: '#f8f4fe', accent: '#a855f7' },
    ];
    // Photoshop / Illustrator : gris medium classique
    const THEMES_CLASSIC = [
        // Classic Forge : les gris du Classique mis en conformité avec la
        // charte (accent Encre dorée) — thème par défaut de l'application.
        // « Classic Forge » était le seul libellé anglais de cet écran, juste
        // au-dessus d'un thème nommé « Classique ». Seul le nom affiché change :
        // l'`id` reste `forge-classic-forge`, c'est lui qui est persisté et lu
        // par `data-theme`.
        { id: 'forge-classic-forge', name: 'Forge classique (défaut)', color: '#686870', accent: '#f59e0b' },
        { id: 'forge-classic', name: 'Classique', color: '#6e6e73', accent: '#2680eb' },
    ];

    return (
        // modal-backdrop = fond sombre fixe quel que soit le thème
        <div className="fixed inset-0 z-[140] flex items-center justify-center modal-backdrop backdrop-blur-md p-4 font-mono text-sm">
            <div className="bg-forge-bg/95 backdrop-blur-md border border-forge-border/20 w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[85vh] relative">

                <button onClick={onClose} className="absolute top-4 right-4 z-50 bg-forge-surface/50 hover:bg-forge-rose/30 text-forge-muted hover:text-forge-rose w-8 h-8 rounded-lg flex items-center justify-center transition-all text-sm">✕</button>

                {/* Header */}
                <div className="px-6 py-4 border-b border-forge-border/15 flex items-center gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-forge-mauve"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
                    <h2 className="text-[12px] font-bold text-forge-text uppercase tracking-[0.2em]">Préférences</h2>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
                    <div className="grid grid-cols-2 gap-6">

                        {/* ── Col 1 : Document ── */}
                        <div className="space-y-5">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-forge-sky/60"></div>
                                <p className="text-[10px] text-forge-sky/80 font-bold uppercase tracking-[0.15em]">Document</p>
                            </div>

                            {/* Dimensions */}
                            <div className="bg-black/20 border border-white/5 rounded-xl p-4 space-y-3 shadow-inner">
                                <div>
                                    <label className="text-[9px] text-forge-sky/60 font-bold uppercase tracking-[0.1em] mb-2 block">Dimensions (L × H)</label>
                                    <div className="flex items-center gap-2">
                                        <NumInput value={canvas.width_mm} onChange={(v) => setCanvas({ width_mm: v })} min={10} />
                                        <span className="text-forge-sky/30 text-[10px] font-bold">×</span>
                                        <NumInput value={canvas.height_mm} onChange={(v) => setCanvas({ height_mm: v })} min={10} />
                                        <span className="text-forge-sky/40 text-[9px] font-bold">mm</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[9px] text-forge-sky/60 font-bold uppercase tracking-[0.1em] mb-1.5 block">Fond</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={canvas.color_bg || '#FFFFFF'} onChange={(e) => setCanvas({ color_bg: e.target.value })} className="w-7 h-7 rounded shrink-0 cursor-pointer p-0 border-0 bg-transparent ring-1 ring-forge-sky/20" />
                                        <span className="text-[10px] text-white/80 tracking-widest font-mono font-bold">{canvas.color_bg || '#FFFFFF'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Limites */}
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-forge-rose/60"></div>
                                <p className="text-[10px] text-forge-rose/80 font-bold uppercase tracking-[0.15em]">Limites</p>
                            </div>

                            <div className="space-y-2">
                                <div className="bg-black/20 border border-white/5 rounded-lg p-3 shadow-inner flex items-center justify-between">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={canvas.showMargin !== false} onChange={e => setCanvas({ showMargin: e.target.checked })} className="accent-[rgba(var(--forge-sky))] w-3 h-3 cursor-pointer" />
                                        <span className="text-forge-sky font-bold text-[10px] uppercase tracking-widest">Repère de marge</span>
                                    </label>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-forge-sky/70 font-bold text-[9px] uppercase tracking-widest" title="Distance de sécurité depuis le bord du format fini.">Marge intérieure</span>
                                        <NumInput value={canvas.margin_mm ?? 3} onChange={(v) => setCanvas({ margin_mm: v })} min={0} />
                                        <span className="text-forge-sky/40 text-[9px]">mm</span>
                                    </div>
                                </div>
                                {/* Deux réglages distincts, présentés avant comme un seul sous
                                    le libellé « Fond perdu » : la case ne montre/cache que le
                                    trait de coupe rouge, tandis que la valeur en mm fait
                                    réellement déborder le document. Décocher laissait donc un
                                    document débordé sans plus aucun repère de coupe, l'inverse
                                    de ce que la case laissait attendre. Chaque contrôle porte
                                    désormais le nom de ce qu'il fait. */}
                                <div className="bg-black/20 border border-white/5 rounded-lg p-3 shadow-inner flex items-center justify-between gap-2">
                                    <label className="flex items-center gap-2 cursor-pointer" title="Affiche ou masque le trait de coupe rouge sur le plan de travail. Le débordement réel du document se règle avec la valeur en mm.">
                                        <input type="checkbox" checked={canvas.showBleed !== false} onChange={e => setCanvas({ showBleed: e.target.checked })} className="accent-[rgba(var(--forge-rose))] w-3 h-3 cursor-pointer" />
                                        <span className="text-forge-rose font-bold text-[10px] uppercase tracking-widest">Trait de coupe</span>
                                    </label>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-forge-rose/70 font-bold text-[9px] uppercase tracking-widest" title="Débordement réel du document, au-delà du format fini.">Fond perdu</span>
                                        <NumInput value={canvas.bleed_mm ?? 0} onChange={(v) => setCanvas({ bleed_mm: v })} min={0} />
                                        <span className="text-forge-rose/40 text-[9px]">mm</span>
                                    </div>
                                </div>
                                {/* Proposé seulement sans fond perdu déclaré : c'est
                                    la signature d'un format qui l'a avalé. */}
                                {!(canvas.bleed_mm > 0) && <SortirFondPerdu />}
                            </div>
                        </div>

                        {/* ── Col 2 : Affichage + Thème ── */}
                        <div className="space-y-5">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-forge-mint/60"></div>
                                <p className="text-[10px] text-forge-mint/80 font-bold uppercase tracking-[0.15em]">Affichage</p>
                            </div>

                            <div className="bg-black/20 border border-white/5 rounded-xl p-4 shadow-inner space-y-0.5">
                                <Toggle label="Règles millimétriques" checked={canvas.showRulers || false} onChange={v => setCanvas({ showRulers: v })} />
                                <Toggle label="Grille (10mm)" checked={canvas.showGrid || false} onChange={v => setCanvas({ showGrid: v })} />
                                <Toggle label="Guides centraux" checked={canvas.showGuides || false} onChange={v => setCanvas({ showGuides: v })} />
                                <hr className="border-forge-mint/10 my-1.5" />
                                <Toggle label="Aimantation grille" checked={canvas.snapToGrid ?? true} onChange={v => setCanvas({ snapToGrid: v })} />
                                <Toggle label="Aimantation guides" checked={canvas.snapToGuides ?? true} onChange={v => setCanvas({ snapToGuides: v })} />
                                <hr className="border-forge-mint/10 my-1.5" />
                                <div title="À l'import d'une fiche réglementaire, à son rafraîchissement et à l'application d'un modèle .lfgr, le cadre de chaque calque se resserre sur son contenu — le geste Cmd/Ctrl+Alt+F, joué tout seul. Décochez pour garder les cadres tels qu'ils arrivent.">
                                    <Toggle label="Ajuster les cadres à l'import" checked={autoFitOnImport !== false} onChange={v => setAutoFitOnImport(v)} />
                                </div>
                                <div className="mt-2 bg-black/15 border border-white/5 rounded-lg p-2 flex gap-2 items-center">
                                    <span className="text-[8px] border border-forge-lemon/30 bg-black/30 px-1.5 py-0.5 rounded text-forge-lemon font-mono font-bold">ALT</span>
                                    <p className="text-[9px] text-forge-muted/50 tracking-widest uppercase">Débrayage libre</p>
                                </div>
                            </div>

                            {/* Thème */}
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-forge-mauve/60"></div>
                                <p className="text-[10px] text-forge-mauve/80 font-bold uppercase tracking-[0.15em]">Thème</p>
                            </div>

                            {(() => {
                                // Helper pour rendre une grille de themes
                                const renderThemeButton = (theme) => (
                                    <button key={theme.id} onClick={() => setUiTheme(theme.id)} title={theme.name}
                                        className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg transition-all duration-200 border ${uiTheme === theme.id ? 'bg-forge-mauve/12 border-forge-mauve/35 scale-105 shadow-[0_0_15px_rgba(var(--forge-mauve),0.1),inset_0_0_10px_rgba(var(--forge-mauve),0.05)]' : 'bg-forge-surface/20 border-forge-border/10 hover:bg-forge-mauve/8 hover:border-forge-mauve/25 hover:shadow-[0_0_12px_rgba(var(--forge-mauve),0.06)] hover:scale-[1.03]'}`}>
                                        <div className="w-8 h-8 rounded-full border border-forge-border/20 relative overflow-hidden" style={{ backgroundColor: theme.color }}>
                                            <div className="w-1/2 h-full absolute right-0 opacity-40 blur-[2px]" style={{ backgroundColor: theme.accent }}></div>
                                        </div>
                                        <span className={`text-[9px] font-bold uppercase tracking-widest truncate w-full text-center ${uiTheme === theme.id ? 'text-forge-mauve' : 'text-forge-muted/60'}`}>{theme.name}</span>
                                    </button>
                                );
                                return (
                                    <div className="space-y-3">
                                        {/* Sombres */}
                                        <div>
                                            <p className="text-[8px] text-forge-muted/50 font-bold uppercase tracking-[0.2em] mb-1.5">Sombre</p>
                                            <div className="grid grid-cols-4 gap-2">
                                                {THEMES_DARK.map(renderThemeButton)}
                                            </div>
                                        </div>
                                        {/* Clairs */}
                                        <div>
                                            <p className="text-[8px] text-forge-muted/50 font-bold uppercase tracking-[0.2em] mb-1.5">Clair</p>
                                            <div className="grid grid-cols-4 gap-2">
                                                {THEMES_LIGHT.map(renderThemeButton)}
                                            </div>
                                        </div>
                                        {/* Classique (Photoshop/Illustrator) */}
                                        <div>
                                            <p className="text-[8px] text-forge-muted/50 font-bold uppercase tracking-[0.2em] mb-1.5">Classique</p>
                                            <div className="grid grid-cols-4 gap-2">
                                                {THEMES_CLASSIC.map(renderThemeButton)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Section pleine largeur — récupération réglementaire FDS */}
                    <div className="mt-6 pt-6 border-t border-white/5">
                        <RegulatoryTokenSection />
                    </div>

                    {/* Section pleine largeur — clé d'agent : voir, régénérer, couper */}
                    <div className="mt-6 pt-6 border-t border-white/5">
                        <AgentKeySection />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-forge-border/15 bg-forge-bg/40 flex justify-end">
                    <button onClick={onClose} className="px-6 py-2.5 bg-forge-mauve/15 text-forge-mauve border border-forge-mauve/25 rounded-lg shadow-[0_0_12px_rgba(var(--forge-mauve),0.08)] hover:bg-forge-mauve/30 hover:border-forge-mauve/40 hover:shadow-[0_0_25px_rgba(var(--forge-mauve),0.2)] hover:scale-[1.03] transition-all duration-200 uppercase tracking-[0.15em] font-bold text-[10px]">Fermer</button>
                </div>
            </div>
        </div>
    );
}
